/**
 * Creating and copying a game.
 *
 * `createGame` is the only way to make a `GameState`, and `cloneGameState` the
 * only way to copy one. Everything else in the engine mutates the state it is
 * handed: a step is a few dozen cell writes, and allocating a fresh board per
 * tick would cost far more than it buys. Callers that need a snapshot — the
 * server, a replay, a test — take one explicitly.
 */
import { cloneBoard, createBoard } from './board.js';
import { createConfig } from './config.js';
import { cloneRng, createRng, nextInt } from './rng.js';
import { refillQueue } from './bag.js';
import { spawnPiece } from './piece.js';
import type { CreateGameOptions, GameState } from './types.js';

/**
 * A new game, ready for its first tick.
 *
 * Everything random about it comes from `options.seed`: the garbage hole
 * column is drawn first, then the queue is filled, then the first piece
 * spawns — the same order as the reference prototype, so the same seed gives
 * the same opening.
 *
 * `options.slots` gives the well more than one active piece — co-op's
 * `shared` and `wide` (`docs/DESIGN.md`, "Co-op"). The slots are sized before
 * anything spawns, because `spawnColumn` divides the well by how many there
 * are, and they are then filled **in slot order from the one shared queue**:
 * two players in one well draw alternately from one 7-bag, so the bag's
 * fairness is a property of the team rather than of either player.
 * `docs/decisions/0029`.
 */
export function createGame(options: CreateGameOptions): GameState {
  const config = createConfig(options);
  const slots = Math.max(1, Math.floor(options.slots ?? 1));
  const state: GameState = {
    config,
    board: createBoard(config.width, config.visibleRows, config.hiddenRows),
    rng: createRng(config.seed),
    queue: [],
    pieces: new Array<null>(slots).fill(null),
    holds: new Array<null>(slots).fill(null),
    holdAvailable: new Array<boolean>(slots).fill(true),
    tick: 0,
    lines: 0,
    attack: 0,
    combo: -1,
    backToBack: false,
    pendingGarbage: [],
    garbageColumn: 0,
    piecesPlaced: 0,
    bestChain: 0,
    topOut: false,
    nextGarbageId: 1,
  };

  state.garbageColumn = nextInt(state.rng, config.width);
  refillQueue(state);
  for (let slot = 0; slot < slots; slot++) spawnPiece(state, slot, null);
  return state;
}

/** A deep copy. The copy shares only the frozen config with the original. */
export function cloneGameState(state: GameState): GameState {
  return {
    config: state.config,
    board: cloneBoard(state.board),
    rng: cloneRng(state.rng),
    queue: [...state.queue],
    pieces: state.pieces.map((piece) => (piece === null ? null : { ...piece })),
    holds: [...state.holds],
    holdAvailable: [...state.holdAvailable],
    tick: state.tick,
    lines: state.lines,
    attack: state.attack,
    combo: state.combo,
    backToBack: state.backToBack,
    pendingGarbage: state.pendingGarbage.map((entry) => ({ ...entry })),
    garbageColumn: state.garbageColumn,
    piecesPlaced: state.piecesPlaced,
    bestChain: state.bestChain,
    topOut: state.topOut,
    nextGarbageId: state.nextGarbageId,
  };
}
